import type { SchoolType } from '@mathflat/domain/SchoolSystem'
import { getKoreanSchoolType, SCHOOL_TYPE } from '@mathflat/domain/SchoolSystem'
import type { StudentSummary } from '@mathflat/domain/Student'
import type { WorksheetAssignee } from '@mathflat/domain/Worksheet'

export type GradeGroup = {
  key: string
  label: string
  assignees: WorksheetAssignee[]
}

const UNKNOWN_GRADE = { key: 'UNKNOWN', label: '기타', order: Number.POSITIVE_INFINITY }

const gradeOf = (student: Pick<StudentSummary, 'schoolType' | 'grade'>) => ({
  key: `${student.schoolType}-${student.grade}`,
  label: `${getKoreanSchoolType(student.schoolType)}${student.grade}`,
  order: SCHOOL_TYPE.indexOf(student.schoolType as SchoolType) * 10 + student.grade,
})

/** 재원 학생만 학년별로 묶는다. 학년은 초·중·고, 학년 순이고 한 학년 안은 이름순이다. */
export const groupAssigneesByGrade = (
  assignees: WorksheetAssignee[],
  students: Pick<StudentSummary, 'id' | 'schoolType' | 'grade'>[],
): GradeGroup[] => {
  const studentById = new Map(students.map((student) => [student.id, student]))
  const groups = new Map<string, GradeGroup & { order: number }>()

  for (const assignee of assignees) {
    if (assignee.studentStatus !== 'ACTIVE') continue
    const student = studentById.get(assignee.studentId)
    const grade = student ? gradeOf(student) : UNKNOWN_GRADE
    const group = groups.get(grade.key) ?? { ...grade, assignees: [] }
    group.assignees.push(assignee)
    groups.set(grade.key, group)
  }

  return [...groups.values()]
    .sort((a, b) => a.order - b.order)
    .map(({ key, label, assignees: members }) => ({
      key,
      label,
      assignees: [...members].sort((a, b) => a.studentName.localeCompare(b.studentName, 'ko')),
    }))
}
